/**
 * Pure helpers for the CITY PULSE strip — extracted from KpiStrip.tsx for
 * unit testing.
 *
 * The strip is the first thing in the rail, so two rules matter more here
 * than anywhere else:
 *
 *   1. A feed that has not answered is not evidence that nothing is
 *      happening. A dead traffic feed printed "0 — NOMINAL", which is a
 *      false all-clear in the one place a reader looks first.
 *   2. A colour must never contradict the number beside it. The accent bar
 *      was fixed by position — red on the first tile, green on the third —
 *      so a tile reading "CLEAR" carried a red bar and a tile reading
 *      "MODERATE" carried a green one.
 */

import type { FallbackTier } from "@chonburi/shared";

/**
 * What a tile may claim about the feed it is built from.
 *
 * "loading" is not "clear" and "unavailable" is not "clear": a zero from a
 * feed that never answered is an absence of data, not an absence of reports.
 */
export interface KpiFeedState {
  /** False when the feed has not answered — the tile must not show a number. */
  known: boolean;
  /** The word to print instead of a status, or null when the value speaks. */
  word: string | null;
}

export function feedState(tier: FallbackTier | "loading" | undefined): KpiFeedState {
  if (tier === "loading") return { known: false, word: "LOADING" };
  if (tier === "unavailable") return { known: false, word: "NO DATA" };
  if (tier === "scenario") return { known: false, word: "SCENARIO" };
  if (tier === "reference") return { known: false, word: "REFERENCE" };
  if (tier === "cache" || tier === "database") return { known: false, word: "CACHED" };
  return { known: tier === "live", word: tier === "live" ? null : "NO DATA" };
}

/** How a count reads. "unknown" means the feed did not answer. */
export type KpiLevel = "critical" | "elevated" | "open" | "clear" | "unknown";

export function reportsLevel(openReports: number, known: boolean): KpiLevel {
  if (!known) return "unknown";
  if (openReports > 20) return "critical";
  if (openReports > 5) return "elevated";
  if (openReports > 0) return "open";
  return "clear";
}

export function trafficLevel(totalEvents: number, known: boolean): KpiLevel {
  if (!known) return "unknown";
  if (totalEvents > 15) return "critical";
  if (totalEvents > 5) return "elevated";
  if (totalEvents > 0) return "open";
  return "clear";
}

/** The word printed beside the number. */
export const LEVEL_WORD: Record<KpiLevel, string> = {
  critical: "CRITICAL",
  elevated: "ELEVATED",
  open: "OPEN",
  clear: "CLEAR",
  unknown: "NO DATA",
};
